import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ApiError,
  getInfo,
  postConsent,
  postStart,
} from '../lib/api';
import type { InfoResponseValid } from '../types';
import { releaseStream } from '../lib/recorder';
import ConsentScreen from '../components/ConsentScreen';
import PermissionScreen from '../components/PermissionScreen';
import InterviewSession from '../components/InterviewSession';
import CompletedScreen from '../components/CompletedScreen';
import ExpiredScreen from '../components/ExpiredScreen';
import ErrorScreen from '../components/ErrorScreen';
import { Card, CardContent } from '../components/ui/Card';
import Shell from '../components/ui/Shell';

type Stage =
  | { kind: 'loading' }
  | { kind: 'invalid'; reason: 'expired' | 'unknown' | 'completed' }
  | { kind: 'consent'; info: InfoResponseValid }
  | { kind: 'permission'; info: InfoResponseValid }
  | { kind: 'session'; info: InfoResponseValid; stream: MediaStream }
  | { kind: 'completed'; info: InfoResponseValid }
  | { kind: 'error'; message: string };

export default function InterviewPage() {
  const params = useParams();
  const token = params.token ?? '';
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  // Track stream so we can release it on unmount.
  const streamRef = useRef<MediaStream | null>(null);

  const loadInfo = useCallback(async () => {
    if (!token) {
      setStage({ kind: 'invalid', reason: 'unknown' });
      return;
    }
    setStage({ kind: 'loading' });
    try {
      const info = await getInfo(token);
      if (!info.valid) {
        setStage({ kind: 'invalid', reason: info.reason });
        return;
      }
      if (info.completed) {
        setStage({ kind: 'completed', info });
        return;
      }
      if (info.consented) {
        setStage({ kind: 'permission', info });
        return;
      }
      setStage({ kind: 'consent', info });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not load the interview.';
      setStage({ kind: 'error', message });
    }
  }, [token]);

  useEffect(() => {
    void loadInfo();
  }, [loadInfo]);

  useEffect(
    () => () => {
      releaseStream(streamRef.current);
      streamRef.current = null;
    },
    [],
  );

  const handleAgree = useCallback(async () => {
    if (stage.kind !== 'consent') return;
    await postConsent(token);
    setStage({ kind: 'permission', info: stage.info });
  }, [stage, token]);

  const handlePermissionGranted = useCallback(
    async (stream: MediaStream) => {
      if (stage.kind !== 'permission') return;
      streamRef.current = stream;
      try {
        const start = await postStart(token);
        setStage({
          kind: 'session',
          info: { ...stage.info, current_idx: start.current_idx, num_questions: start.num_questions },
          stream,
        });
      } catch (err) {
        releaseStream(stream);
        streamRef.current = null;
        const message =
          err instanceof Error ? err.message : 'Could not start the interview.';
        setStage({ kind: 'error', message });
      }
    },
    [stage, token],
  );

  const handleCompleted = useCallback(() => {
    if (stage.kind !== 'session') return;
    releaseStream(streamRef.current);
    streamRef.current = null;
    setStage({ kind: 'completed', info: stage.info });
  }, [stage]);

  switch (stage.kind) {
    case 'loading':
      return (
        <Shell centered maxWidth="narrow">
          <Card>
            <CardContent>
              <p className="text-center text-sm text-muted-foreground">
                Loading interview…
              </p>
            </CardContent>
          </Card>
        </Shell>
      );
    case 'invalid':
      return <ExpiredScreen reason={stage.reason} />;
    case 'completed':
      return <CompletedScreen candidateName={stage.info.candidate_name} />;
    case 'consent':
      return (
        <ConsentScreen
          candidateName={stage.info.candidate_name}
          numQuestions={stage.info.num_questions}
          consentTextMd={stage.info.consent_text_md}
          onAgree={handleAgree}
        />
      );
    case 'permission':
      return <PermissionScreen onGranted={handlePermissionGranted} />;
    case 'session':
      return (
        <InterviewSession
          token={token}
          startIdx={stage.info.current_idx}
          numQuestions={stage.info.num_questions}
          stream={stage.stream}
          onCompleted={handleCompleted}
        />
      );
    case 'error':
      return (
        <ErrorScreen
          message={stage.message}
          onRetry={() => void loadInfo()}
          hint="If your link is still within its expiration window, retrying should work."
        />
      );
  }
}
